import { useState, useEffect } from 'react';
import { getAdminRoomTypeDetail, patchAdminRoomType } from '@/API/apiService';

export interface RoomFormData {
  site_controller_id: string;
  site_controller_room_type_id: string;
  chillnn_id: string;
  room_type_name: string;
  room_type_name_en: string;
  capacity: string;
  room_area: string;
  floor_plan: string;
  description: string;
  description_en: string;
  bath_amenity: string;
  bath_amenity_en: string;
  price: string;
  floor_plan_image?: { image_id: number | null; url: string | null } | File;
  sort_number: string;
  area_id: string;
  information: string;
  information_en: string;
  property_id: string;
  property_name: string;
  property_name_en: string;
  region_id: string;
  room_type_id: string;
  facility: string;
  facility_en: string;
  is_published: boolean;
}

export interface UseRoomEditor {
  formData: RoomFormData;
  propertyName: string;
  images: string[]; // URL 配列（既存＋新規）
  imageIds: (number | null)[]; // 同じインデックスで既存画像のIDまたは null（新規画像）
  loading: boolean;
  handleInputChange: (
    field: keyof RoomFormData,
    value: string | boolean | any
  ) => void;
  handleAddImages: (files: File[]) => void;
  handleRemoveImage: (index: number) => void;
  handleSaveAll: () => Promise<void>;
}

export const useRoomEditor = (
  propertyId?: string,
  roomId?: string
): UseRoomEditor => {
  const [formData, setFormData] = useState<RoomFormData>({
    site_controller_id: '',
    site_controller_room_type_id: '',
    chillnn_id: '',
    room_type_name: '',
    room_type_name_en: '',
    capacity: '',
    room_area: '',
    floor_plan: '',
    description: '',
    description_en: '',
    bath_amenity: '',
    bath_amenity_en: '',
    price: '',
    floor_plan_image: { image_id: null, url: null },
    sort_number: '',
    area_id: '',
    information: '',
    information_en: '',
    property_id: '',
    property_name: '',
    property_name_en: '',
    region_id: '',
    room_type_id: '',
    facility: '',
    facility_en: '',
    is_published: false,
  });

  const [propertyName, setPropertyName] = useState<string>('');
  const [images, setImages] = useState<string[]>([]);
  const [imageIds, setImageIds] = useState<(number | null)[]>([]);
  const [newImages, setNewImages] = useState<File[]>([]);
  // 追加: 削除対象の既存画像のIDを記録する state
  const [deleteImageIds, setDeleteImageIds] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchRoomData = async () => {
      if (!propertyId || !roomId) {
        setLoading(false);
        return;
      }
      try {
        const response = await getAdminRoomTypeDetail(propertyId, roomId);
        const data = response.data.data;

        setPropertyName(data.property_name || '');
        setFormData({
          site_controller_id: data.site_controller_id
            ? data.site_controller_id.toString()
            : '',
          site_controller_room_type_id: data.site_controller_room_type_id || '',
          chillnn_id: data.chillnn_id || '',
          room_type_name: data.room_type_name || '',
          room_type_name_en: data.room_type_name_en || '',
          capacity: data.capacity ? data.capacity.toString() : '',
          room_area: data.room_area ? data.room_area.toString() : '',
          floor_plan: data.floor_plan || '',
          description: data.description || '',
          description_en: data.description_en || '',
          bath_amenity: data.bath_amenity || '',
          bath_amenity_en: data.bath_amenity_en || '',
          price: data.price ? data.price.toString() : '',
          floor_plan_image: data.floor_plan_image || {
            image_id: null,
            url: null,
          },
          sort_number: data.sort_number ? data.sort_number.toString() : '',
          area_id: data.area_id ? data.area_id.toString() : '',
          information: data.information || '',
          information_en: data.information_en || '',
          property_id: data.property_id ? data.property_id.toString() : '',
          property_name: data.property_name || '',
          property_name_en: data.property_name_en || '',
          region_id: data.region_id ? data.region_id.toString() : '',
          room_type_id: data.room_type_id ? data.room_type_id.toString() : '',
          facility: data.facility || '',
          facility_en: data.facility_en || '',
          is_published: data.is_published ?? false,
        });

        if (Array.isArray(data.images)) {
          const urls = data.images.map((img: any) => img.url ?? '');
          const ids = data.images.map((img: any, idx: number) => {
            const idVal = img.id ?? img.image_id;
            const idNum = Number(idVal);
            return !isNaN(idNum) && idNum > 0 ? idNum : idx;
          });
          setImages(urls);
          setImageIds(ids);
          console.log('取得した既存画像 IDs:', ids);
        }
      } catch (error) {
        console.error('Error fetching room data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRoomData();
  }, [propertyId, roomId]);

  const handleInputChange = (
    field: keyof RoomFormData,
    value: string | boolean | any
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddImages = (files: File[]) => {
    const blobUrls = files.map((file) => URL.createObjectURL(file));
    setImages((prev) => [...prev, ...blobUrls]);
    setImageIds((prev) => [...prev, ...files.map(() => null)]);
    setNewImages((prev) => [...prev, ...files]);
    console.log('新規画像追加後の images:', [...images, ...blobUrls]);
    console.log('新規画像追加後の imageIds:', [
      ...imageIds,
      ...files.map(() => null),
    ]);
  };

  // 画像削除時、既存画像の場合は削除対象IDを deleteImageIds に追加する
  const handleRemoveImage = (index: number) => {
    setImages((prevImages) => {
      const newArr = [...prevImages];
      const removedUrl = newArr.splice(index, 1)[0];
      console.log('画像削除対象:', removedUrl);
      console.log('画像削除後の images:', newArr);

      setImageIds((prevIds) => {
        const newIds = [...prevIds];
        const removedId = newIds.splice(index, 1)[0];
        console.log(
          '削除された画像ID:',
          removedId !== undefined && removedId !== null
            ? removedId
            : 'new image'
        );
        if (removedId !== undefined && removedId !== null) {
          setDeleteImageIds((prev) => [...prev, removedId]);
        }
        return newIds;
      });

      if (removedUrl && removedUrl.startsWith('blob:')) {
        const countBlobBefore = prevImages
          .slice(0, index)
          .filter((url) => url.startsWith('blob:')).length;
        setNewImages((prevFiles) => {
          const newFiles = [...prevFiles];
          if (countBlobBefore < newFiles.length) {
            newFiles.splice(countBlobBefore, 1);
          }
          return newFiles;
        });
      }
      return newArr;
    });
  };

  // API に合わせ、削除対象画像と新規追加画像の情報も送信するよう更新
  const handleSaveAll = async () => {
    if (!propertyId || !roomId) {
      throw new Error('propertyId or roomId not specified.');
    }

    const formDataToSend = new FormData();

    // API のキーに合わせた基本項目の送信
    formDataToSend.append('propertyId', propertyId);
    formDataToSend.append('roomTypeId', roomId);
    formDataToSend.append('site_controller_id', formData.site_controller_id);
    formDataToSend.append(
      'site_controller_room_type_id',
      formData.site_controller_room_type_id
    );
    formDataToSend.append('chillnn_id', formData.chillnn_id);
    // room_type_name を name に、room_type_name_en を name_en に変換して送信
    formDataToSend.append('name', formData.room_type_name);
    formDataToSend.append('name_en', formData.room_type_name_en);
    formDataToSend.append('capacity', formData.capacity);
    formDataToSend.append('room_area', formData.room_area);
    formDataToSend.append('floor_plan', formData.floor_plan);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('description_en', formData.description_en);
    formDataToSend.append('facility', formData.facility);
    formDataToSend.append('facility_en', formData.facility_en);
    formDataToSend.append('bath_amenity', formData.bath_amenity);
    formDataToSend.append('bath_amenity_en', formData.bath_amenity_en);
    formDataToSend.append('information', formData.information);
    formDataToSend.append('information_en', formData.information_en);
    formDataToSend.append('sort_number', formData.sort_number);
    formDataToSend.append(
      'is_published',
      formData.is_published ? 'true' : 'false'
    );
    formDataToSend.append('price', formData.price);

    // 新規追加画像の送信 (キーを "add_images[]" として送信)
    newImages.forEach((file) => {
      formDataToSend.append('add_images[]', file);
    });

    // 削除対象の既存画像の ID を送信
    deleteImageIds.forEach((id, i) => {
      if (id !== undefined && id !== null) {
        formDataToSend.append(`delete_images[${i}]`, id.toString());
      }
    });

    // floor_plan_image の送信（単一画像）
    if (formData.floor_plan_image instanceof File) {
      formDataToSend.append('floor_plan_image', formData.floor_plan_image);
    }

    console.log('--- FormData payload ---');
    const entries = Array.from(formDataToSend.entries());
    for (const pair of entries) {
      console.log(pair[0] + ': ' + pair[1]);
    }
    console.log('------------------------');

    try {
      const response = await patchAdminRoomType(
        propertyId,
        roomId,
        formDataToSend
      );
      alert('部屋情報の更新に成功しました。');
    } catch (error) {
      console.error('部屋更新に失敗しました:', error);
      alert('部屋更新に失敗しました');
    }
  };

  return {
    formData,
    propertyName,
    images,
    imageIds,
    loading,
    handleInputChange,
    handleAddImages,
    handleRemoveImage,
    handleSaveAll,
  };
};

export default useRoomEditor;
